import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { isObservable, Observable, Subscription } from 'rxjs';

/**
 * Standard "one submission at a time" guard for save/create buttons.
 *
 * Repeated clicks on a Save button used to fire the request more than once, which on
 * read-then-insert endpoints (program registration especially) produced duplicate records.
 * Every create/save screen now drives its button from the same processing flag so the
 * behaviour is identical everywhere:
 *
 *   <button (click)="save()" [appProcessing]="processing$">Create</button>
 *
 * While the flag is true the button is disabled and shows the standard spinner. The host
 * component is responsible for setting the flag back to false on BOTH success and failure —
 * a failed save must re-enable the button so the user can retry.
 *
 * The spinner is prepended rather than swapped in for the label, so buttons whose text is
 * itself an Angular binding (e.g. "{{ isEditMode ? 'Update' : 'Create' }}") keep working.
 */
@Directive({
  selector: '[appProcessing]',
  standalone: true
})
export class ProcessingButtonDirective implements OnChanges, OnDestroy {
  @Input('appProcessing') processing: Observable<boolean> | boolean | null = false;

  private sub?: Subscription;
  private spinner?: HTMLElement;

  constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['processing']) return;

    this.sub?.unsubscribe();
    this.sub = undefined;

    if (isObservable(this.processing)) {
      this.sub = this.processing.subscribe((busy) => this.apply(!!busy));
    } else {
      this.apply(!!this.processing);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private apply(busy: boolean): void {
    const host = this.el.nativeElement;

    if (busy) {
      this.renderer.setProperty(host, 'disabled', true);
      this.renderer.setAttribute(host, 'aria-busy', 'true');
      this.addSpinner(host);
    } else {
      this.renderer.setProperty(host, 'disabled', false);
      this.renderer.removeAttribute(host, 'aria-busy');
      this.removeSpinner(host);
    }
  }

  private addSpinner(host: HTMLElement): void {
    if (this.spinner) return;
    const spinner = this.renderer.createElement('span');
    this.renderer.addClass(spinner, 'spinner-border');
    this.renderer.addClass(spinner, 'spinner-border-sm');
    this.renderer.addClass(spinner, 'me-1');
    this.renderer.setAttribute(spinner, 'role', 'status');
    this.renderer.setAttribute(spinner, 'aria-hidden', 'true');
    this.renderer.insertBefore(host, spinner, host.firstChild);
    this.spinner = spinner;
  }

  private removeSpinner(host: HTMLElement): void {
    if (!this.spinner) return;
    this.renderer.removeChild(host, this.spinner);
    this.spinner = undefined;
  }
}
